export type JsonLogicRule = Record<string, unknown>;

/**
 * Map JSON Logic variable names back to UI condition types
 * These must match the API's actual variable names
 */
const VAR_TO_CONDITION_MAP: Record<string, string> = {
  cluster_name: 'Cluster',
  node_group_name: 'Node group',
  instance_id: 'Instance ID',
  tags: 'Volume Tag',
  instance_tags: 'Instance tag',
};

/**
 * Parse a JSON Logic rule back to UI format
 * This extracts conditions from the JSON Logic structure
 */
export interface ParsedCondition {
  type: string; // cluster, nodeGroup, instance, volumeTag, tags
  operator: string; // ==, in, some, none, etc.
  values: (string | Record<string, unknown>)[]; // Can be strings or objects (for volume tags)
}

export function parseJsonLogicRule(rule: JsonLogicRule): ParsedCondition[] {
  // console.log('🔍 parseJsonLogicRule input:', rule);
  const conditions: ParsedCondition[] = [];

  function extractCondition(logic: unknown, depth = 0): void {
    if (!logic || typeof logic !== 'object') {
      // console.log(`${_indent}⚠️ Not an object or null`);
      return;
    }

    const obj = logic as Record<string, unknown>;

    // Handle "and" operator - extract all conditions
    if ('and' in obj && Array.isArray(obj.and)) {
      // console.log(`${_indent}✅ Found 'and' with ${obj.and.length} conditions`);
      obj.and.forEach((cond) => extractCondition(cond, depth + 1));
      return;
    }

    // Handle "or" operator - extract all conditions
    if ('or' in obj && Array.isArray(obj.or)) {
      // console.log(`${_indent}✅ Found 'or' with ${obj.or.length} conditions`);
      obj.or.forEach((cond) => extractCondition(cond, depth + 1));
      return;
    }

    // Handle "some" operator
    // Tags format: { "some": [{ "var": "tags" }, { "in": [{ "var": "" }, [values]] }] } or { "or": [{in: [val, {"var": ""}]}] }
    // Single field contains format: { "some": [[patterns], { "in": [{ "var": "" }, { "var": "field" }] }] }
    if ('some' in obj && Array.isArray(obj.some)) {
      // console.log(`${indent}✅ Found 'some' operator:`, obj.some);
      const [first, condition] = obj.some;

      // Check if first element is a var (tags case) or an array (single field contains case)
      if (first && typeof first === 'object' && 'var' in first) {
        const varName = (first as { var: string }).var;

        // Tags exact match: { "in": [{ "var": "" }, [values]] }
        if (condition && typeof condition === 'object' && 'in' in condition) {
          const inArray = (condition as { in: unknown[] }).in;
          if (Array.isArray(inArray) && inArray.length >= 2) {
            const [inFirst, inSecond] = inArray;

            // Exact match format: [{ "var": "" }, [values]]
            if (
              inFirst &&
              typeof inFirst === 'object' &&
              'var' in inFirst &&
              Array.isArray(inSecond)
            ) {
              const filteredValues = inSecond.filter(
                (v) => v && typeof v === 'string' && v.trim() !== ''
              ) as string[];

              if (filteredValues.length > 0) {
                conditions.push({
                  type: varName,
                  operator: 'some',
                  values: filteredValues,
                });
              }
            }
          }
        }
        // Tags contains match: { "or": [{ "in": [pattern, { "var": "" }] }, ...] }
        else if (condition && typeof condition === 'object' && 'or' in condition) {
          const orArray = (condition as { or: unknown[] }).or;
          if (Array.isArray(orArray)) {
            const values: string[] = [];

            orArray.forEach((item) => {
              if (item && typeof item === 'object' && 'in' in item) {
                const inArray = (item as { in: unknown[] }).in;
                if (Array.isArray(inArray) && inArray.length >= 2) {
                  const [pattern] = inArray;
                  if (typeof pattern === 'string') {
                    values.push(pattern);
                  }
                }
              }
            });

            if (values.length > 0) {
              conditions.push({
                type: varName,
                operator: 'some-contains',
                values,
              });
            }
          }
        }
      }
      // Single field contains format: [[patterns], { "in": [{ "var": "" }, { "var": "field" }] }]
      else if (
        Array.isArray(first) &&
        condition &&
        typeof condition === 'object' &&
        'in' in condition
      ) {
        const inArray = (condition as { in: unknown[] }).in;
        if (Array.isArray(inArray) && inArray.length >= 2) {
          const [inFirst, inSecond] = inArray;

          if (
            inFirst &&
            typeof inFirst === 'object' &&
            'var' in inFirst &&
            inSecond &&
            typeof inSecond === 'object' &&
            'var' in inSecond
          ) {
            const varName = (inSecond as { var: string }).var;
            const patterns = first.filter((v) => typeof v === 'string') as string[];

            if (patterns.length > 0) {
              conditions.push({
                type: varName,
                operator: 'some-contains',
                values: patterns,
              });
            }
          }
        }
      }
      return;
    }

    // Handle "none" operator - exclude/doesn't contain
    // Tags format: { "none": [{ "var": "tags" }, { "in": [{ "var": "" }, [values]] }] } or { "or": [{in: [val, {"var": ""}]}] }
    // Single field doesn't contain format: { "none": [[patterns], { "in": [{ "var": "" }, { "var": "field" }] }] }
    if ('none' in obj && Array.isArray(obj.none)) {
      // console.log(`${indent}✅ Found 'none' operator:`, obj.none);
      const [first, condition] = obj.none;

      // Check if first element is a var (tags case) or an array (single field contains case)
      if (first && typeof first === 'object' && 'var' in first) {
        const varName = (first as { var: string }).var;

        // Tags exact exclude: { "in": [{ "var": "" }, [values]] }
        if (condition && typeof condition === 'object' && 'in' in condition) {
          const inArray = (condition as { in: unknown[] }).in;
          if (Array.isArray(inArray) && inArray.length >= 2) {
            const [inFirst, inSecond] = inArray;

            // Exact match format: [{ "var": "" }, [values]]
            if (
              inFirst &&
              typeof inFirst === 'object' &&
              'var' in inFirst &&
              Array.isArray(inSecond)
            ) {
              const filteredValues = inSecond.filter(
                (v) => v && typeof v === 'string' && v.trim() !== ''
              ) as string[];

              if (filteredValues.length > 0) {
                conditions.push({
                  type: varName,
                  operator: 'none',
                  values: filteredValues,
                });
              }
            }
          }
        }
        // Tags doesn't contain: { "or": [{ "in": [pattern, { "var": "" }] }, ...] }
        else if (condition && typeof condition === 'object' && 'or' in condition) {
          const orArray = (condition as { or: unknown[] }).or;
          if (Array.isArray(orArray)) {
            const values: string[] = [];

            orArray.forEach((item) => {
              if (item && typeof item === 'object' && 'in' in item) {
                const inArray = (item as { in: unknown[] }).in;
                if (Array.isArray(inArray) && inArray.length >= 2) {
                  const [pattern] = inArray;
                  if (typeof pattern === 'string') {
                    values.push(pattern);
                  }
                }
              }
            });

            if (values.length > 0) {
              conditions.push({
                type: varName,
                operator: 'none-contains',
                values,
              });
            }
          }
        }
      }
      // Single field doesn't contain format: [[patterns], { "in": [{ "var": "" }, { "var": "field" }] }]
      else if (
        Array.isArray(first) &&
        condition &&
        typeof condition === 'object' &&
        'in' in condition
      ) {
        const inArray = (condition as { in: unknown[] }).in;
        if (Array.isArray(inArray) && inArray.length >= 2) {
          const [inFirst, inSecond] = inArray;

          if (
            inFirst &&
            typeof inFirst === 'object' &&
            'var' in inFirst &&
            inSecond &&
            typeof inSecond === 'object' &&
            'var' in inSecond
          ) {
            const varName = (inSecond as { var: string }).var;
            const patterns = first.filter((v) => typeof v === 'string') as string[];

            if (patterns.length > 0) {
              conditions.push({
                type: varName,
                operator: 'none-contains',
                values: patterns,
              });
            }
          }
        }
      }
      return;
    }

    // Handle "!" (NOT) operator - used for "is not" conditions
    // Format: { "!": { "in": [{ "var": "varName" }, [values]] } }
    if ('!' in obj) {
      const negatedCondition = obj['!'];
      if (
        negatedCondition &&
        typeof negatedCondition === 'object' &&
        'in' in negatedCondition &&
        Array.isArray((negatedCondition as { in: unknown[] }).in)
      ) {
        const inArray = (negatedCondition as { in: unknown[] }).in;
        const [first, second] = inArray;

        // Check if first element is the var
        if (first && typeof first === 'object' && 'var' in first) {
          const varName = (first as { var: string }).var;
          const values = Array.isArray(second) ? second : [second];
          const condition = {
            type: varName,
            operator: '!',
            values: values.map(String),
          };
          conditions.push(condition);
          return;
        }
      }

      // Handle "!" with "some" for negated volume tags
      if (
        negatedCondition &&
        typeof negatedCondition === 'object' &&
        'some' in negatedCondition &&
        Array.isArray((negatedCondition as { some: unknown[] }).some)
      ) {
        const someArray = (negatedCondition as { some: unknown[] }).some;
        const [varObj, condition] = someArray;

        if (varObj && typeof varObj === 'object' && 'var' in varObj) {
          const varName = (varObj as { var: string }).var;

          if (condition && typeof condition === 'object' && 'in' in condition) {
            const inArray = (condition as { in: unknown[] }).in;
            if (Array.isArray(inArray) && inArray.length >= 2) {
              let values: unknown[] = [];
              const [first, second] = inArray;

              // Format 1: ["tagValue", { "var": "" }] - value first, var second
              if (
                typeof first === 'string' &&
                second &&
                typeof second === 'object' &&
                'var' in second
              ) {
                values = [first];
              }
              // Format 2: [{ "var": "" }, ["tagValue"]] - var first, array of values second
              else if (
                first &&
                typeof first === 'object' &&
                'var' in first &&
                Array.isArray(second)
              ) {
                values = second;
              }
              // Fallback: try to extract any string values
              else {
                values = inArray.filter((v) => typeof v === 'string');
              }

              // Filter out empty strings and objects
              const filteredValues = values.filter(
                (v) => v && typeof v === 'string' && v.trim() !== ''
              ) as string[];

              if (filteredValues.length > 0) {
                const parsedCondition = {
                  type: varName,
                  operator: '!',
                  values: filteredValues,
                };
                conditions.push(parsedCondition);
              }
            }
          }
        }
        return;
      }
    }

    // Handle "in" operator: can be either format
    // Format 1: { "in": [value, { "var": "varName" }] }
    // Format 2: { "in": [{ "var": "varName" }, [values]] }
    if ('in' in obj && Array.isArray(obj.in)) {
      // console.log(`${indent}✅ Found 'in' operator:`, obj.in);
      const [first, second] = obj.in;

      // Check if first element is the var
      if (first && typeof first === 'object' && 'var' in first) {
        const varName = (first as { var: string }).var;
        const values = Array.isArray(second) ? second : [second];
        const condition = {
          type: varName,
          operator: 'in',
          values: values.map(String),
        };
        // console.log(`${indent}✅ Extracted condition (var first):`, condition);
        conditions.push(condition);
        return;
      }

      // Check if second element is the var
      if (second && typeof second === 'object' && 'var' in second) {
        const varName = (second as { var: string }).var;
        const values = Array.isArray(first) ? first : [first];
        const condition = {
          type: varName,
          operator: 'in',
          values: values.map(String),
        };
        // console.log(`${indent}✅ Extracted condition (var second):`, condition);
        conditions.push(condition);
        return;
      }

      // console.log(`${indent}⚠️ 'in' operator doesn't match expected format`);
    }

    // Handle "==" operator: { "==": [{ "var": "varName" }, value] }
    if ('==' in obj && Array.isArray(obj['=='])) {
      // console.log(`${indent}✅ Found '==' operator:`, obj['==']);
      const [varObj, value] = obj['=='];
      if (varObj && typeof varObj === 'object' && 'var' in varObj) {
        const varName = (varObj as { var: string }).var;
        const condition = {
          type: varName,
          operator: '==',
          values: [String(value)],
        };
        // console.log(`${indent}✅ Extracted condition (==):`, condition);
        conditions.push(condition);
      }
      return;
    }

    // console.log(`${indent}⚠️ No matching operator found in:`, Object.keys(obj));
  }

  extractCondition(rule);
  // console.log('🔍 parseJsonLogicRule output:', conditions);
  return conditions;
}

/**
 * Convert parsed conditions to display format
 */
export function formatConditionForDisplay(condition: ParsedCondition): {
  type: string;
  values: (string | Record<string, unknown>)[];
} {
  return {
    type: VAR_TO_CONDITION_MAP[condition.type] || condition.type,
    values: condition.values,
  };
}
